import { useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";

import { ListLoader, LoaderWithOverlay, Icon } from "components/atoms";
import { ExpenseTableItem } from "components/molecules";
import { ColumnSettingsModal } from "components/organisms";
import { useColumnPreferences } from "hooks/useColumnPreferences";

import { Expense } from "interfaces/expenses";
import { IconName } from "interfaces/icons";

import {
	getExpenses,
	getExpensesCount,
	getExpensesList,
	getExpensesLoading,
	getExpensesTotal,
} from "state/modules/expenses";

import { MAIN_THEME } from "styles/colors";
import { FONT_FAMILY_MEDIUM } from "styles/typography";

const SettingsButton = styled.button`
	border: none;
	background: transparent;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 10px;
	margin-left: auto;

	&:hover {
		opacity: 0.8;
	}
`;

const ContentHeader = styled.div`
	display: flex;
	justify-content: flex-end;
`;

const DEFAULT_COLUMNS = [
	{ id: 'property', label: 'Property', isVisible: true, width: '1fr' },
	{ id: 'name', label: 'Name', isVisible: true, width: '1fr' },
	{ id: 'description', label: 'Description', isVisible: true, width: '1.3fr' },
	{ id: 'amount', label: 'Amount', isVisible: true, width: '0.9fr' },
	{ id: 'currency', label: 'Currency', isVisible: true, width: '0.8fr' },
	{ id: 'date', label: 'Date', isVisible: true, width: '0.8fr' },
	{ id: 'receipt', label: 'Receipt', isVisible: true, width: '0.5fr' },
];

const List = styled(InfiniteScroll as any)`
	display: grid;
	grid-template-columns: 1fr;
	width: 100%;
	gap: 1px;
	border-bottom-right-radius: 16px;
	border-bottom-left-radius: 16px;
	overflow: hidden !important;
	position: relative;
`;

const Container = styled.div`
	padding: 15px 30px 40px;
	overflow-y: auto;
	
	@media (max-width: 768px) {
		padding: 15px 10px 40px;
	}
`;

const ScrollWrapper = styled.div`
	width: 100%;
	overflow-x: auto;
	overflow-y: visible;
	
	/* Custom scrollbar styling */
	&::-webkit-scrollbar {
		height: 8px;
	}
	
	&::-webkit-scrollbar-track {
		background: ${MAIN_THEME.DARK_BG};
		border-radius: 4px;
	}
	
	&::-webkit-scrollbar-thumb {
		background: ${MAIN_THEME.DARK_BG_TERTIARY};
		border-radius: 4px;
		
		&:hover {
			background: ${MAIN_THEME.GREY};
		}
	}
`;

const TableWrapper = styled.div<{ mobileMinWidth: string }>`
	min-width: 900px;

	@media (max-width: 768px) {
		min-width: ${(props) => props.mobileMinWidth};
	}
`;


const TableHeaderItem = styled.div`
		font-size: 14px;
		text-align: left;
		display: flex;
		justify-content: flex-start;
		align-items: center;

		height: 38px;
		padding: 10px 8px;
		color: ${MAIN_THEME.TEXT_SECONDARY};
		background: transparent;



		font-weight: 600;
		text-transform: none;
		letter-spacing: 0.3px;


		@media (max-width: 768px) {
			font-size: 12px;
			height: 36px;
			padding: 8px 6px;
		}

`;

const TableHeader = styled.div<{ columns?: string }>`
	display: grid;
	grid-template-columns: ${(props) => props.columns || "1fr 1fr 1.3fr 0.9fr 0.8fr 0.8fr 0.5fr"};
	gap: 1px;
	margin-bottom: 1px;
	border-top-right-radius: 16px;
	border-top-left-radius: 16px;
	overflow: hidden;
	background: transparent;
	border-bottom: 1px solid ${MAIN_THEME.BORDER_COLOR};

	@media (max-width: 768px) {
		display: grid;
	}
`;

const EmptyListMessage = styled.p`
	font-family: ${FONT_FAMILY_MEDIUM};
	color: ${MAIN_THEME.TEXT_SECONDARY};
	font-size: 16px;
	background: ${MAIN_THEME.DARK_BG_TERTIARY};
	border: 1px solid ${MAIN_THEME.BORDER_COLOR};
	border-radius: 8px;
	padding: 15px;
	text-align: center;
`;

interface Props {
	showProperty?: boolean;
}

const ExpensesList = (props: Props): JSX.Element => {
	const { showProperty } = props;
	const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

	const { columns, toggleColumn } = useColumnPreferences("expensesList", DEFAULT_COLUMNS);

	const visibleColumns = useMemo(() => {
		return columns.filter(col => {
			if (!showProperty && col.id === 'property') return false;
			return col.isVisible;
		});
	}, [columns, showProperty]);

	const visibleColumnIds = useMemo(() => visibleColumns.map(c => c.id), [visibleColumns]);

	const gridTemplateColumns = useMemo(() => {
		return visibleColumns.map(c => c.width).join(" ");
	}, [visibleColumns]);

	const mobileMinWidth = useMemo(() => {
		return `${Math.max(visibleColumns.length * 120, 100)}px`;
	}, [visibleColumns]);

	const isColumnVisible = (id: string) => visibleColumnIds.includes(id);

	const expensesList = useSelector(getExpensesList);
	const expensesTotal = useSelector(getExpensesTotal);
	const expensesCount = useSelector(getExpensesCount);
	const isLoading = useSelector(getExpensesLoading);

	const dispatch = useDispatch();

	const hasMore = useMemo(
		() => expensesCount < expensesTotal,
		[expensesCount, expensesTotal]
	);

	const loadMore = () => {
		if (!isLoading) {
			dispatch(getExpenses(true));
		}
	};

	const renderExpensesList = () => (
		<List
			scrollableTarget="propertyDetailsListContainer"
			dataLength={expensesCount}
			next={loadMore}
			hasMore={hasMore}
			loader={<ListLoader />}
		>
			{expensesList.map((expense: Expense) => (
				<ExpenseTableItem
					showProperty={showProperty}
					data={expense}
					key={expense.id}
					visibleColumnIds={visibleColumnIds}
					gridTemplateColumns={gridTemplateColumns}
				/>
			))}
			{isLoading ? <LoaderWithOverlay /> : null}
		</List>
	);

	const renderTableHeader = () =>
		expensesList.length ? (
			<TableHeader columns={gridTemplateColumns}>
				{isColumnVisible('property') ? <TableHeaderItem>Property</TableHeaderItem> : null}
				{isColumnVisible('name') && <TableHeaderItem>Name</TableHeaderItem>}
				{isColumnVisible('description') && <TableHeaderItem>Description</TableHeaderItem>}
				{isColumnVisible('amount') && <TableHeaderItem>Amount</TableHeaderItem>}
				{isColumnVisible('currency') && <TableHeaderItem>Currency</TableHeaderItem>}
				{isColumnVisible('date') && <TableHeaderItem>Date</TableHeaderItem>}
				{isColumnVisible('receipt') && <TableHeaderItem>Receipt</TableHeaderItem>}
			</TableHeader>
		) : (
			<EmptyListMessage>Empty list</EmptyListMessage>
		);

	return (
		<Container>
			<ContentHeader>
				<SettingsButton onClick={() => setIsSettingsModalOpen(true)}>
					<Icon name={IconName.SETTINGS} color={MAIN_THEME.TEXT_PRIMARY} size={20} />
				</SettingsButton>
			</ContentHeader>
			<ScrollWrapper>
				<TableWrapper mobileMinWidth={mobileMinWidth}>
					{renderTableHeader()}
					{renderExpensesList()}
				</TableWrapper>
			</ScrollWrapper>
			<ColumnSettingsModal
				isVisible={isSettingsModalOpen}
				close={() => setIsSettingsModalOpen(false)}
				columns={columns.filter(c => showProperty || c.id !== 'property')}
				onToggle={toggleColumn}
			/>
		</Container>
	);
};

export default ExpensesList;
